/**
 * WONE-565 (Phase 4b-3): /auth/callback — OAuth 2.1 + PKCE landing page.
 *
 * Platform's /o/authorize?idp_hint hands the user off to the IdP, then
 * redirects back here with ?code=... &state=... once the IdP returns.
 * We exchange the code for tokens via /o/token (Platform's
 * WebRefreshCookieTokenView sets the __Secure-wone_refresh cookie
 * server-side; the access token comes back in the JSON body), then
 * load the user profile and dispatch to home or the originally-
 * intended URL.
 *
 * Error cases (state mismatch, IdP error, token exchange failure)
 * bounce back to /signin with an error toast — the AuthProvider's
 * completeSSOLogin handler owns that recovery.
 */
import React, { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "providers/auth";
import { peekSSOProvider } from "providers/auth-tokens";
import { clearStorage } from "utility/Function";
import { PageLoading } from "apps/user/learner/components/page-container";
import { trackEvent } from "utility/mixpanel";

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { completeSSOLogin } = useAuth();
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");

    // The IdP picked at the chooser, stashed on the PKCE session state
    // so completions/errors can be segmented per IdP (WONE-712: SSO
    // events carry ``Provider``). Peek before completeSSOLogin — the
    // token exchange consumes the sessionStorage entry.
    const provider = peekSSOProvider() ?? "unknown";

    if (error) {
      trackEvent("Auth SSO Error", { Provider: provider, Code: error });
      navigate(`/signin?error=${encodeURIComponent(error)}`);
      return;
    }
    if (!code || !state) {
      trackEvent("Auth SSO Error", {
        Provider: provider,
        Code: "missing_authorization_code",
      });
      navigate("/signin?error=missing_authorization_code");
      return;
    }

    // Wipe any role-keyed localStorage from a previous user before
    // letting completeSSOLogin overwrite only the keys for the new
    // user's role. Without this, role-exclusive flags (teacherID /
    // companyID / paymentLink) leak across users on a shared device.
    clearStorage();

    completeSSOLogin(code, state, "SSO")
      .then(() => {
        trackEvent("Auth SSO Completed", { Provider: provider });
      })
      .catch(() => {
        // completeSSOLogin owns failure handling — it reports to Sentry,
        // fires the "Attempting to Sign In Failed" event with the real
        // error, and navigates to /email-entry. This catch only prevents
        // the unhandled-rejection warning; re-stamping a guessed error
        // code here would just bury the real cause.
      });
  }, [params, completeSSOLogin, navigate]);

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      // WONE-612: cover the PatternBackground canvas with cream so the
      // SSO round-trip loader doesn't flash blue while /o/token runs.
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "#F4EFEA",
        zIndex: 1,
      }}
    >
      <PageLoading />
    </div>
  );
};

export default AuthCallback;
